import { Label } from '@/components/ui/label'
import { ExpectedBreakdown } from './ExpectedBreakdown'
import { NumericKeypad } from './NumericKeypad'
import { TenderPair } from './TenderPair'
import { TenderVerdict } from './TenderVerdict'
import type { ExpectedBreakdownSpec, TenderPhrasing } from './tenderMath'

/**
 * One counting step: the "We expect | You typed" pair, a one-line verdict,
 * the arithmetic behind the expected figure one tap away, and a keypad.
 *
 * The keypad is the point (F-CTRL-N33). Cash always had one; card and online
 * had two bare inputs, so on a touchscreen till — no physical keyboard — their
 * totals literally could not be typed, and Skip was the only way forward.
 *
 * `expected` omitted = a blind count (cash): the pair shows only the typed
 * cell, and no verdict or breakdown, so the operator cannot copy the figure.
 * F-FE-N139 replaced the expected sentence / amber cross-day callout /
 * reconciliation line with this shape.
 */
export function TenderCountStep({
  id,
  label,
  value,
  onChange,
  formatCurrency,
  expected,
  delta,
  threshold = 0,
  phrasing,
  breakdown,
  idleText = 'Type the total to compare',
}: {
  id: string
  label: string
  value: string
  onChange: (next: string) => void
  formatCurrency: (n: number) => string
  /** The figure the count is checked against. Omit for a blind count. */
  expected?: number
  /** counted − expected, or undefined until a number has been typed. */
  delta?: number
  threshold?: number
  phrasing?: TenderPhrasing
  breakdown?: ExpectedBreakdownSpec
  idleText?: string
}) {
  const blind = expected === undefined
  return (
    <div className="space-y-3">
      <Label className="text-base">{label}</Label>
      <TenderPair
        id={id}
        expected={blind ? null : expected}
        value={value}
        onChange={onChange}
        formatCurrency={formatCurrency}
      />
      {!blind && (
        <TenderVerdict
          delta={delta}
          threshold={threshold}
          formatCurrency={formatCurrency}
          phrasing={phrasing}
          idleText={idleText}
        />
      )}
      {!blind && breakdown && (
        <ExpectedBreakdown
          summary={`Where does ${formatCurrency(expected)} come from?`}
          spec={breakdown}
          formatCurrency={formatCurrency}
        />
      )}
      <NumericKeypad value={value} onChange={onChange} className="pt-1" />
    </div>
  )
}
